import { useState, useEffect } from 'react';
import { WorkflowProps, BioScoreResult } from '@/types/workflow';
import {
	NoiseAnalysisData,
	runNoiseAnalysis,
	visualizeNoiseHeatMap,
	removeNoiseHeatMap,
} from '@/services/forma/forma-noise-analysis';
import NoiseAnalysisBioScore, { AnalysisResult, SpaceTypeKey } from '@/services/score/noise';
import { Select } from '@/components/UI/Select/Select';
import { WeaveButton } from '@/components/UI/WeaveButton/WeaveButton';
import { WeaveBanner } from '@/components/UI/WeaveBanner/WeaveBanner';
import { Icon } from '@/components/UI/Icon/Icon';
import styles from './styles.module.scss';
import SkeletonLoader from '@/components/UI/SkeletonLoader/SkeletonLoader';
import { WeaveAccordion } from '@/components/UI/WeaveAccordion/WeaveAccordion';
import { unitConversionService as unitService } from '@/services/unit-conversion.service';

interface StatData {
	label: string;
	value: number;
	color: string;
	icon: string;
}

interface GroundNoiseData {
	label: string;
	key: string;
	color: string;
	percent: number;
	value: number;
}

const NoiseAnalysis: React.FC<WorkflowProps> = ({
	onDataChange,
	workflowData,
	activeSection,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [heatMapVisible, setHeatMapVisible] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [analysisData, setAnalysisData] = useState<NoiseAnalysisData>();
	const [bioScoreResult, setBioScoreResult] = useState<BioScoreResult<AnalysisResult>>({
		data: workflowData[activeSection]?.data ?? null,
	});

	const [selectedSpaceType, setSelectedSpaceType] = useState<SpaceTypeKey>(
		workflowData[activeSection]?.data?.spaceType || SpaceTypeKey.Commercial
	);

	const noiseAnalysisBioScore = new NoiseAnalysisBioScore();

	const buildScoreData = (analysisData: NoiseAnalysisData) => {
		const { standardBands, statistics } = analysisData;
		const bands = [
			{
				range: '<55',
				averageNoise: Math.round(statistics.min),
				area: standardBands['<55dB']?.m2 ?? 0,
				percentage: standardBands['<55dB']?.percent ?? 0,
			},
			{
				range: '55-65',
				averageNoise: Math.round(statistics.avg),
				area: standardBands['55–65dB']?.m2 ?? 0,
				percentage: standardBands['55–65dB']?.percent ?? 0,
			},
			{
				range: '>65',
				averageNoise: Math.round(statistics.max),
				area: standardBands['>65dB']?.m2 ?? 0,
				percentage: standardBands['>65dB']?.percent ?? 0,
			},
		];

		// Keep scoring focused on bands that actually exist on site.
		const nonEmptyBands = bands.filter((band) => band.area > 0 && band.percentage > 0);
		return nonEmptyBands.length > 0 ? nonEmptyBands : bands;
	};

	const getNoiseData = async () => {
		setIsLoading(true);
		setError(null);

		try {
			const data = await runNoiseAnalysis([55, 65, 75]);
			setAnalysisData(data);

			const scoreData = buildScoreData(data);
			const result = noiseAnalysisBioScore.analyze(selectedSpaceType, scoreData);

			setBioScoreResult({
				data: result,
			});
		} catch (err) {
			console.error('Error running noise analysis:', err);
			setError('Failed to run noise analysis. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleSpaceTypeChange = (value: string) => {
		const newSpaceType = value as SpaceTypeKey;
		setSelectedSpaceType(newSpaceType);
		updateBioScore(newSpaceType);
	};

	const updateBioScore = (spaceType: SpaceTypeKey) => {
		if (!analysisData) return;

		const scoreData = buildScoreData(analysisData);
		const result = noiseAnalysisBioScore.analyze(spaceType, scoreData);

		setBioScoreResult({
			data: result,
		});
	};

	const getTextColor = (bg: string): string => {
		const brightColors = ['#fff344ff', '#f0c848ff'];
		return brightColors.includes(bg.toLowerCase()) ? 'black' : 'white';
	};

	const handleToggleHeatMap = async () => {
		if (heatMapVisible) {
			await removeNoiseHeatMap();
		} else {
			await visualizeNoiseHeatMap();
		}
		setHeatMapVisible((v) => !v);
	};

	useEffect(() => {
		if (bioScoreResult.data && analysisData) {
			setSelectedSpaceType(bioScoreResult.data.spaceType || SpaceTypeKey.Commercial);
		} else if (!analysisData) {
			getNoiseData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		onDataChange({
			data: bioScoreResult.data,
		});
	}, [bioScoreResult, onDataChange]);

	const renderBioScore = () => {
		if (!analysisData || !bioScoreResult.data) return null;

		const { data } = bioScoreResult;
		const groundNoiseData: GroundNoiseData[] = [
			{
				label: '<55',
				key: '<55',
				color: '#589409',
				percent: analysisData.standardBands['<55dB'].percent,
				value: unitService.toPreferredUnit(
					analysisData.standardBands['<55dB'].m2,
					'area',
					'm2'
				),
			},
			{
				label: '55–65',
				key: '55–65',
				color: '#FCE500',
				percent: analysisData.standardBands['55–65dB'].percent,
				value: unitService.toPreferredUnit(
					analysisData.standardBands['55–65dB'].m2,
					'area',
					'm2'
				),
			},
			{
				label: '>65',
				key: '>65',
				color: '#FF0000',
				percent: analysisData.standardBands['>65dB'].percent,
				value: unitService.toPreferredUnit(
					analysisData.standardBands['>65dB'].m2,
					'area',
					'm2'
				),
			},
		];

		return (
			<div className={styles.noiseScoreContainer}>
				<div className={styles.spaceTypeSelect}>
					<label htmlFor="spaceTypeSelect" className={styles.spaceTypeLabel}>
						Select the intended final use of this zone
					</label>
					<p className={styles.spaceTypeHint}>
						This choice drives target noise level, STC expectation, and final BioScore.
					</p>
					<div className={styles.selectWrapper}>
						<Select
							options={Object.keys(SpaceTypeKey).map((key) => ({
								label: key,
								value: SpaceTypeKey[key as keyof typeof SpaceTypeKey],
							}))}
							value={selectedSpaceType}
							onChange={(event) => handleSpaceTypeChange(event.detail.value)}
						/>
					</div>
				</div>

				<WeaveAccordion label="BioScore" expanded>
					<div className={styles.accordionContent}>
						<div className={styles.bioScoreContainer}>
							<div className={styles.bioScoreMainCard}>
								<div className={styles.mainCardContent}>
									<span className={styles.mainCardLabel}>Weighted Score</span>
									<span className={styles.mainCardValue}>
										{Math.round(data.weightedScore)}/10
									</span>
								</div>
							</div>

							<div className={styles.bioScoreSummary}>
								<div className={styles.summaryCard}>
									<div className={styles.summaryCardHeader}>
										<h4 className={styles.summaryCardTitle}>STC Rating</h4>
										<span className={styles.summaryCardValue}>
											{data.stcRating}
										</span>
									</div>
									<div className={styles.summaryCardDetails}>
										<div className={styles.summaryDetailRow}>
											<span className={styles.detailLabel}>Target Noise:</span>
											<span className={styles.detailValue}>
												{data.targetNoise} dBA
											</span>
										</div>
										<div className={styles.summaryDetailRow}>
											<span className={styles.detailLabel}>Noise Reduction:</span>
											<span className={styles.detailValue}>
												{data.noiseReduction} dB
											</span>
										</div>
										<div className={styles.summaryDetailRow}>
											<span className={styles.detailLabel}>Penalty:</span>
											<span className={styles.detailValue}>{data.penalty}</span>
										</div>
									</div>
								</div>

								<div className={styles.summaryCard}>
									<div className={styles.summaryCardHeader}>
										<h4 className={styles.summaryCardTitle}>Noise Environment</h4>
									</div>
									<div className={styles.summaryCardDetails}>
										<div className={styles.summaryDetailRow}>
											<span className={styles.detailLabel}>
												Exterior Noise (avg):
											</span>
											<span className={styles.detailValue}>
												{Math.round(data.exteriorNoise)} dBA
											</span>
										</div>
										<div className={styles.summaryDetailRow}>
											<span className={styles.detailLabel}>
												Interior Noise (avg):
											</span>
											<span className={styles.detailValue}>
												{Math.round(data.interiorNoise)} dBA
											</span>
										</div>
										<div className={styles.summaryDetailRow}>
											<span className={styles.detailLabel}>Variance:</span>
											<span className={styles.detailValue}>
												{Math.round(data.variance)} dB
											</span>
										</div>
									</div>
								</div>

								<div className={styles.summaryCard}>
									<div className={styles.summaryCardHeader}>
										<h4 className={styles.summaryCardTitle}>Assessment</h4>
									</div>
									<div className={styles.summaryCardDetails}>
										<div className={styles.summaryDetailRow}>
											<span className={styles.detailLabel}>Performance:</span>
											<span
												className={`${styles.detailValue} ${styles.label}`}
												style={{
												backgroundColor: data.scoreColor,
												color: getTextColor(data.scoreColor),
											}}
										>
												<span>{data.overallPerformance}</span>
											</span>
										</div>
										<div className={`${styles.summaryDetailRow} ${styles.compact}`}>
											<span className={styles.detailLabel}>Action:</span>
											<span className={styles.detailValue}>{data.action}</span>
										</div>
										<div className={`${styles.summaryDetailRow} ${styles.compact}`}>
											<span className={styles.detailLabel}>Recommendation:</span>
											<span className={styles.detailValue}>
												{data.recommendation}
											</span>
										</div>
									</div>
								</div>
							</div>

							<div className={styles.bioScoreZones}>
								<h3 className={styles.zonesTitle}>Noise Zones Analysis</h3>
								<div className={styles.zonesList}>
									{data.zones.map((zone) => (
										<div key={zone.zone} className={styles.zoneItem}>
											<div className={styles.zoneHeader}>
												<div
													className={styles.zoneIndicator}
													style={{
													backgroundColor: zone.color,
													color: getTextColor(zone.color),
												}}
											/>
												<h4 className={styles.zoneLabel}>Zone: {zone.zone}</h4>
												<span className={styles.zoneScore}>
													{zone.score}/10
												</span>
											</div>

											<div className={styles.zoneDetails}>
												<div className={styles.zoneDetailRow}>
													<span className={styles.detailLabel}>
														Exterior Noise:
													</span>
													<span className={styles.detailValue}>
														{zone.exteriorNoise} dB
													</span>
												</div>
												<div className={styles.zoneDetailRow}>
													<span className={styles.detailLabel}>
														Interior Noise:
													</span>
													<span className={styles.detailValue}>
														{zone.interiorNoise} dB
													</span>
												</div>
												<div className={styles.zoneDetailRow}>
													<span className={styles.detailLabel}>
														Variance:
													</span>
													<span className={styles.detailValue}>
														{zone.variance} dB
													</span>
												</div>
												<div className={styles.zoneDetailRow}>
													<span className={styles.detailLabel}>
														Performance:
													</span>
													<span className={styles.detailValue}>
														{zone.performance}
													</span>
												</div>
												<div className={styles.zoneDetailRow}>
													<span className={styles.detailLabel}>Action:</span>
													<span className={styles.detailValue}>
														{zone.action}
													</span>
												</div>
											</div>
										</div>
								))}
								</div>
							</div>
						</div>
					</div>
				</WeaveAccordion>

				<WeaveAccordion label="Ground Noise">
					<div className={styles.accordionContent}>
						<div className={styles.groundNoiseChart}>
							<div className={styles.groundNoiseHeader}>
								<span>dB(A)</span>
								<span className={styles.groundNoiseHeaderArea}>
									{unitService.getUnitAbbreviation('area')}
								</span>
								<span></span>
								<span className={styles.groundNoiseHeaderPercent}>%</span>
							</div>
							<div className={styles.groundNoiseRows}>
								{groundNoiseData.map((row) => (
									<div key={row.key} className={styles.groundNoiseRow}>
										<span className={styles.groundNoiseDb}>{row.label}</span>
										<span className={styles.groundNoiseArea}>
											{row.value.toFixed(0)}
										</span>
										<div className={styles.groundNoiseBarTrack}>
											<div
												className={styles.groundNoiseBarFill}
												style={{
												width: `${Math.max(row.percent, 2)}%`,
												backgroundColor: row.color,
											}}
										/>
										</div>
										<span className={styles.groundNoisePercent}>
											{Math.round(row.percent)}
										</span>
									</div>
							))}
							</div>
						</div>
					</div>
				</WeaveAccordion>

				<WeaveAccordion label="Noise Threshold">
					<div className={styles.accordionContent}>
						<p className={styles.thresholdHint}>
							Shows how much area is above or below each dB limit. Compare with Ground
							Noise bands to identify where mitigation is needed first.
						</p>
						<div className={styles.thresholdsContainer}>
							{[55, 65]
								.filter((threshold) => Boolean(analysisData.thresholds[threshold]))
								.map((threshold) => {
									const thresholdData = analysisData.thresholds[threshold];
									const totalArea = thresholdData.aboveM2 + thresholdData.belowM2;
									const aboveRatio =
										totalArea > 0 ? (thresholdData.aboveM2 / totalArea) * 100 : 0;
									const belowRatio =
										totalArea > 0 ? (thresholdData.belowM2 / totalArea) * 100 : 0;

									return (
										<div key={threshold} className={styles.thresholdItem}>
											<div className={styles.thresholdTitle}>
												<span className={styles.thresholdLabel}>
													Threshold: {threshold} dB
												</span>
											</div>

											<div className={styles.thresholdComparison}>
												<div className={styles.thresholdBar}>
													<div
														className={styles.thresholdAbove}
														style={{ width: `${aboveRatio}%` }}
													/>
													<div
														className={styles.thresholdBelow}
														style={{ width: `${belowRatio}%` }}
													/>
												</div>
											</div>

											<div className={styles.thresholdDetailsGrid}>
												<div className={styles.thresholdDetail}>
													<div className={styles.detailHeader}>
														<span
															className={styles.detailDot}
															data-type="above"
														/>
														<span className={styles.detailLabel}>
															Above {threshold} dB
														</span>
													</div>
													<div className={styles.detailValues}>
														<span className={styles.detailValue}>
															{unitService.formatWithPreferredUnit(
																thresholdData.aboveM2,
																'area',
																'm2'
															)}
														</span>
														<span className={styles.detailPercent}>
															{thresholdData.abovePercent}%
														</span>
													</div>
												</div>

												<div className={styles.thresholdDetail}>
													<div className={styles.detailHeader}>
														<span
															className={styles.detailDot}
															data-type="below"
														/>
														<span className={styles.detailLabel}>
															Below {threshold} dB
														</span>
													</div>
													<div className={styles.detailValues}>
														<span className={styles.detailValue}>
															{unitService.formatWithPreferredUnit(
																thresholdData.belowM2,
																'area',
																'm2'
															)}
														</span>
														<span className={styles.detailPercent}>
															{thresholdData.belowPercent}%
														</span>
													</div>
												</div>
											</div>
										</div>
									);
								})}
						</div>
					</div>
				</WeaveAccordion>

				<WeaveAccordion label="Noise Statistics">
					<div className={styles.accordionContent}>
						<div className={styles.statsContainer}>
							{[
							{
								label: 'Minimum',
								value: analysisData.statistics.min,
								color: '#589409',
								icon: '▼',
							},
							{
								label: 'Average',
								value: analysisData.statistics.avg,
								color: '#FCE500',
								icon: '●',
							},
							{
								label: 'Maximum',
								value: analysisData.statistics.max,
								color: '#FF0000',
								icon: '▲',
							},
						].map((stat: StatData) => {
							const range = analysisData.statistics.max - analysisData.statistics.min;
							const normalizedValue =
								range > 0
									? ((stat.value - analysisData.statistics.min) / range) * 100
									: 50;
							const displayWidth = Math.max(normalizedValue, 10);

							return (
								<div key={stat.label} className={styles.statItem}>
									<div className={styles.statHeader}>
										<span
											className={styles.statIcon}
											style={{ color: stat.color }}
										>
											{stat.icon}
										</span>
										<span className={styles.statLabel}>{stat.label}</span>
										<span className={styles.statValue}>{stat.value} dB</span>
									</div>
									<div className={styles.visualizationBar}>
										<div
											className={styles.visualizationFill}
											style={{
												width: `${displayWidth}%`,
												backgroundColor: stat.color,
											}}
										/>
									</div>
								</div>
							);
						})}
						</div>
					</div>
				</WeaveAccordion>
			</div>
		);
	};

	if (isLoading)
		return <SkeletonLoader title="Loading noise analysis data..." loading={isLoading} />;

	const hasAnalysisData = Boolean(analysisData && 'statistics' in analysisData);

	if (!hasAnalysisData) {
		return (
			<div className={styles.missingDataContainer}>
				<section className={styles.section}>
					<h2 className={styles.title}>Noise analysis is not available yet</h2>

					<p className={styles.text}>
						To retrieve noise analysis data, you must first run the analysis directly in{' '}
						<strong>Autodesk Forma</strong>.
					</p>

					<ol className={styles.list}>
						<li>Run the Noise Analysis for the desired model or scenario</li>
						<li>Wait for Forma to complete the analysis process</li>
						<li>Return here and click refresh button</li>
					</ol>

					<div className={styles.buttonWrapper}>
						<WeaveButton variant="solid" onClick={getNoiseData}>
							Refresh
						</WeaveButton>
					</div>
				</section>

			</div>
		);
	}

	return (
		<>
			<div className={styles.instructions}>
				<div className={styles.instructionsHeader}>
					<h2 className={styles.instructionsTitle}>
						Analyze Noise Levels and Choose Space Type
					</h2>
					<div className={styles.instructionsActions}>
						<WeaveButton
							variant="white"
							onClick={handleToggleHeatMap}
							disabled={!analysisData || !('statistics' in analysisData)}
						>
							<Icon name="forma-eye-16" />
						</WeaveButton>
					</div>
				</div>
				<div className={styles.instructionsBlock}>
					<p className={styles.instructionsText}>
						Use the panels below to analyze noise levels and choose the most appropriate
						space type for this zone:
					</p>
					<ul className={styles.instructionsList}>
						<li>
							<strong>Ground Noise:</strong> area distribution by dB bands (&lt;55,
							55-65, &gt;65)
						</li>
						<li>
							<strong>Noise Threshold:</strong> area above/below each selected dB
							limit
						</li>
						<li>
							<strong>Noise Statistics:</strong> detailed minimum, average, and maximum
							measured levels
						</li>
					</ul>
					<p className={styles.instructionsText}>
						Use{' '}
						<span className={styles.inlineHeatMapHint}>
							<strong>HeatMap</strong>
						</span>{' '}
						(top-right icon) to locate the loudest zones and prioritize mitigation
						before finalizing BioScore.
					</p>
				</div>
			</div>
			{error && <WeaveBanner variant="warning" text={error} />}
			<div>{renderBioScore()}</div>
		</>
	);
};

export default NoiseAnalysis;
